import React, { useState, useRef, useEffect, useMemo, useId } from 'react';
import { MdSearch, MdClose } from 'react-icons/md';
import './searchableSelect.css';

/**
 * SearchableSelect — a combobox: type directly into the visible box to
 * filter, or click to browse the full list. Not "click to open, THEN
 * find a separate search field inside" — the box itself IS the search
 * field, which is what makes it fast for a list of 700 students.
 *
 * Same `options` shape as the plain <Select> in Form.js
 * ([{value, label}] or plain strings), so it drops in anywhere a
 * <Select> is used today without reshaping data.
 *
 * States (all colored so the eye finds the field's condition without
 * reading it):
 *   - empty/unselected -> neutral border, dimmed placeholder
 *   - has a value       -> filled text, a clear (×) button appears
 *   - required + empty  -> danger border (same convention as ui-input)
 *   - no options match   -> a colored "no results" row, not a blank list
 *
 * RTL: no left/right assumptions in the JS; the CSS uses only logical
 * properties (inset-inline-*, margin-inline-*) so it mirrors correctly
 * under the app's RTL <html dir="rtl">. Font: every size/family comes
 * from the --ui-* tokens already on the page, never hardcoded here, so
 * a template-wide font change applies to this control automatically.
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'اختر أو ابحث...',
  error,
  disabled = false,
  clearable = true,
  emptyMessage = 'لا توجد نتائج',
  noOptionsMessage = 'لا توجد خيارات',
}) {
  const normalized = useMemo(
    () => options.map((o) => (typeof o === 'object' ? o : { value: o, label: String(o) })),
    [options]
  );
  const selected = useMemo(
    () => normalized.find((o) => String(o.value) === String(value)) || null,
    [normalized, value]
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();

  // What the box SHOWS: the selected option's label while closed, or
  // whatever the user is typing while open/searching. This is what
  // makes "type to filter" and "shows current selection" coexist in
  // one box instead of two.
  const displayValue = open ? query : (selected ? selected.label : '');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((o) => o.label.toLowerCase().includes(q));
  }, [normalized, query]);

  useEffect(() => { setActiveIndex(0); }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  const openList = () => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const pick = (option) => {
    onChange(option.value);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'Enter', ' '].includes(e.key)) { e.preventDefault(); openList(); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[activeIndex]) pick(filtered[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setQuery('');
        break;
      case 'Tab':
        setOpen(false);
        setQuery('');
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={rootRef}
      className={`ui-ssel${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${error ? ' has-error' : ''}${selected ? ' has-value' : ''}`}
    >
      <span className="ui-ssel-icon ui-ssel-icon-search" aria-hidden="true">
        <MdSearch size={16} />
      </span>

      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        className="ui-ssel-input"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={openList}
        onClick={openList}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onKeyDown={onKeyDown}
      />

      {clearable && selected && !disabled && (
        <button
          type="button"
          className="ui-ssel-clear"
          onClick={clear}
          title="مسح الاختيار"
          aria-label="مسح الاختيار"
        >
          <MdClose size={14} />
        </button>
      )}

      {open && (
        <ul id={listboxId} ref={listRef} className="ui-ssel-list" role="listbox">
          {normalized.length === 0 ? (
            <li className="ui-ssel-empty">{noOptionsMessage}</li>
          ) : filtered.length === 0 ? (
            <li className="ui-ssel-empty">{emptyMessage}</li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={String(o.value)}
                data-idx={i}
                role="option"
                aria-selected={String(o.value) === String(value)}
                className={`ui-ssel-option${String(o.value) === String(value) ? ' is-selected' : ''}${i === activeIndex ? ' is-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
